/**
 * useParallax.js — Hook custom pour l'effet parallax optimisé
 *
 * ============================================================
 * 📚 LEÇON : Le problème du mousemove sans throttle
 * ============================================================
 *
 * L'événement 'mousemove' peut se déclencher jusqu'à 200-300 fois
 * par seconde selon la vitesse de déplacement de la souris.
 *
 * SANS optimisation (comportement précédent) :
 *   mousemove (200x/s)
 *     → setMousePosition (200x/s)    ← setState React
 *       → re-render App (200x/s)     ← tout l'arbre React recalculé
 *         → re-render enfants (200x/s) ← Header, Footer, page courante...
 *
 *   Résultat : 200 renders/seconde inutiles.
 *   Impact mesurable sur l'INP (Interaction to Next Paint) — métrique
 *   Core Web Vitals de Google depuis mars 2024.
 *
 * ============================================================
 * 📚 LEÇON : requestAnimationFrame (rAF)
 * ============================================================
 *
 * Le navigateur rafraîchit l'écran à 60fps max (16.7ms par frame).
 * Calculer + afficher plus de 60x/seconde est un gaspillage pur :
 * les frames intermédiaires ne sont jamais affichées.
 *
 * rAF = "Je veux exécuter ce code au prochain rafraîchissement écran"
 *   → Synchronisé sur le cycle de rendu du navigateur
 *   → Maximum 1 exécution par frame (60fps max)
 *   → Automatiquement mis en pause quand l'onglet est en arrière-plan
 *     (économie de batterie sur mobile !)
 *
 * AVEC rAF :
 *   mousemove (200x/s)
 *     → rafRef.current = true       ← flag simple, pas de setState
 *       → rAF (60x/s max)           ← synchronisé écran
 *         → setMousePosition (60x/s) ← 1 setState par frame max
 *
 *   Résultat : 3-4x moins de renders, INP amélioré.
 *
 * ============================================================
 * 📚 LEÇON : useRef pour les valeurs "muettes"
 * ============================================================
 *
 * On utilise deux useRef dans ce hook :
 *
 *   rafRef      → stocke l'ID du requestAnimationFrame en cours
 *                 Permet de l'annuler au cleanup (cancelAnimationFrame)
 *                 Un setState ici déclencherait un re-render inutile.
 *
 *   pendingRef  → flag booléen "un mousemove est en attente de traitement"
 *                 Évite de créer plusieurs rAF pour la même frame.
 *                 Un setState ici aussi serait contre-productif.
 *
 * Règle : si une valeur change souvent mais n'a pas besoin de déclencher
 * un re-render → useRef. Si un changement doit déclencher un rendu → useState.
 *
 * ============================================================
 *
 * @param {number} [intensity=20] - Amplitude du déplacement en px (défaut: 20)
 * @param {number} [factor=0.5]   - Facteur appliqué à la position (défaut: 0.5)
 * @returns {{ x: number, y: number }} Position normalisée pour le parallax
 *
 * @example
 * const { x, y } = useParallax()
 *
 * return (
 *   <div style={{ transform: `translate(${x}px, ${y}px)` }} />
 * )
 */

import { useState, useEffect, useRef, useCallback } from 'react'

const useParallax = (intensity = 20, factor = 0.5) => {
  // Position calculée — la seule valeur qui déclenche un re-render
  const [position, setPosition] = useState({ x: 0, y: 0 })

  // ID du requestAnimationFrame en cours (pour pouvoir l'annuler au cleanup)
  const rafRef = useRef(null)

  // Flag : indique qu'un mousemove est arrivé et attend d'être traité
  // Évite de créer plusieurs rAF pour la même frame quand la souris
  // bouge très vite (plusieurs events avant le prochain rafraîchissement)
  const pendingRef = useRef(false)

  // Dernières coordonnées de souris reçues
  // useRef car on les lit dans la callback rAF — pas besoin de re-render
  const mouseRef = useRef({ x: 0, y: 0 })

  // ── Callback de mise à jour ────────────────────────────────────────────
  // useCallback évite de recréer cette fonction à chaque render.
  // Elle est passée à requestAnimationFrame — si elle changeait à chaque
  // render, les rAF précédents garderaient une référence "stale".
  const updatePosition = useCallback(() => {
    // On remet le flag à false AVANT le setState
    // (un nouveau mousemove pourrait arriver pendant le setState)
    pendingRef.current = false
    rafRef.current = null

    // Calcul de la position normalisée
    // mouseRef.current contient les coords les plus récentes reçues
    setPosition({
      x: mouseRef.current.x * factor,
      y: mouseRef.current.y * factor,
    })
  }, [factor])

  useEffect(() => {
    const handleMouseMove = (e) => {
      // 1. Stocker les coords brutes dans une ref (pas de re-render)
      mouseRef.current = {
        x: (e.clientX / window.innerWidth - 0.5) * intensity,
        y: (e.clientY / window.innerHeight - 0.5) * intensity,
      }

      // 2. Si un rAF est déjà programmé pour cette frame → on ignore
      //    (la prochaine frame lira les coords les plus récentes)
      if (pendingRef.current) return

      // 3. Sinon, on programme 1 seul rAF pour la prochaine frame
      pendingRef.current = true
      rafRef.current = requestAnimationFrame(updatePosition)
    }

    // Écoute du mouvement souris sur toute la fenêtre
    window.addEventListener('mousemove', handleMouseMove, {
      // passive: true dit au navigateur que cet écouteur n'appellera jamais
      // preventDefault(). Le navigateur peut alors optimiser le scroll
      // sans attendre la fin de l'exécution du handler.
      passive: true,
    })

    return () => {
      // Cleanup : supprimer l'écouteur
      window.removeEventListener('mousemove', handleMouseMove)

      // Annuler le rAF en cours s'il y en a un
      // (évite une mise à jour d'état sur un composant démonté)
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [intensity, updatePosition])

  return position
}

export default useParallax
